
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge class names with tailwind-merge
 * @param {...any} inputs - Class names to merge
 * @returns {string} Merged class names
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Calculate read time for a given text content
 * Assuming average reading speed of 225 words per minute
 * @param {string} content - HTML or plain text content
 * @returns {string} e.g. "5 min read"
 */
export function calculateReadTime(content) {
  if (!content) return "1 min read";
  
  // Strip HTML tags
  const text = content.replace(/<[^>]*>?/gm, '');
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / 225);
  
  return `${minutes} min read`;
}

/**
 * Smoothly scroll to the top of the page with custom duration
 * @param {number} duration - Animation duration in ms (default 500)
 */
export function smoothScrollToTop(duration = 500) {
  const start = window.scrollY;
  const startTime = performance.now();

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Ease out quart (pow 4) for smoother finish
    const ease = 1 - Math.pow(1 - progress, 4);
    
    window.scrollTo(0, start * (1 - ease));

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Format currency amount for display
 * @param {number} amount - Amount to format
 * @param {string} locale - Locale string (default: 'en-IN')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, locale = 'en-IN') {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '0';
  }
  
  // Format with Indian number system (lakhs, crores)
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}
